import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
import { Outlet } from 'react-router-dom'
import { useAdminGenClient } from '../api/genClient'
import { WebAuthnCredentials } from '../pages/WebAuthnCredentials'
import { PamLoading } from '../components/PamStateComponents'

// ---------------------------------------------------------------------------
// Themed enrollment prompt (styled-components + @devnw/theme tokens +
// Phase-3 PamLoading — replaces the previous MUI
// Box/Card/CardContent/CircularProgress/Typography tree). See
// .superpowers/sdd/oidcpam-gate6-shared.md — OIDCPAM Gate-6 Phase 7.
// ---------------------------------------------------------------------------

const Screen = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
`

const PromptCard = styled.div`
  max-width: 480px;
  width: 90%;
  padding: ${({ theme }) => ut(asUnified(theme)).space('lg')};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius() ?? 8}px;
  background: ${({ theme }) => asUnified(theme).bg?.canvas ?? '#ffffff'};
  box-shadow: ${({ theme }) => ut(asUnified(theme)).boxShadow('sm') ?? '0 1px 3px rgba(0,0,0,0.2)'};
`

const PromptTitle = styled.h2`
  margin: 0 0 ${({ theme }) => ut(asUnified(theme)).space('sm')};
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.lg ?? '1.25rem'};
  font-weight: 600;
  color: ${({ theme }) => asUnified(theme).text?.primary ?? '#0f172a'};
`

const PromptBody = styled.p`
  margin: 0 0 ${({ theme }) => ut(asUnified(theme)).space('md')};
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.sm ?? '0.875rem'};
  color: ${({ theme }) => asUnified(theme).text?.secondary ?? '#64748b'};
`

/**
 * Route-wrapper for PAM-section pages (see pamRoutePaths.ts for the exact
 * list). Without a registered passkey, the AAL3 step-up ceremony required by
 * privileged PAM actions (e.g. grant activation, admin_access_requests.go:815)
 * can never succeed — rather than let a user discover that only when an
 * action fails, this checks enrollment status once per mount and, if absent,
 * replaces the ENTIRE PAM subtree (no sidebar, no nav) with a full-screen
 * enrollment prompt, matching the minimalism of the login page.
 */
export const PamPasskeyGate = (): JSX.Element | null => {
  const client = useAdminGenClient()
  const [enrolled, setEnrolled] = useState<boolean | null>(null)

  const checkStatus = useCallback((): void => {
    client
      .webAuthnEnrollmentStatus({})
      .then((resp) => setEnrolled(resp.enrolled))
      .catch(() => setEnrolled(true)) // fail-open on the UI gate: a transient
      // check failure must never trap an already-enrolled user behind a
      // prompt they can't get past; the backend's own fail-closed check
      // (strongFactorEnrolledOrError) is still the real security boundary.
  }, [client])

  useEffect(() => {
    checkStatus()
  }, [])

  if (enrolled === null) {
    return (
      <Screen>
        <PamLoading label="Checking passkey enrollment…" />
      </Screen>
    )
  }

  if (!enrolled) {
    return (
      <Screen>
        <PromptCard>
          <PromptTitle>Set up a passkey to continue</PromptTitle>
          <PromptBody>
            PAM actions in this area require step-up authentication with a
            passkey or security key. Register one to continue.
          </PromptBody>
          <WebAuthnCredentials onRegistered={checkStatus} />
        </PromptCard>
      </Screen>
    )
  }

  return <Outlet />
}
